import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { clearAllImages, fetchTeamMembers, uploadImages } from "../api";
import { useUploadStore } from "../store/useUploadStore";
import { compressImages } from "../utils/imageCompress";

const MAX_FILES = 100;

export function UploadPanel() {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const {
    teamMembers,
    setTeamMembers,
    startUpload,
    setProgress,
    finishUpload,
    failUpload,
    isUploading,
    progress,
    error
  } = useUploadStore();

  const [selectedMember, setSelectedMember] = useState("");
  const [files, setFiles] = useState<File[]>([]);

  useEffect(() => {
    fetchTeamMembers()
      .then((items) => {
        setTeamMembers(items);
        setSelectedMember((current) => current || items[0] || "");
      })
      .catch(() => failUpload("无法加载人员列表"));
  }, [setTeamMembers, failUpload]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList) return;
    const filesArray = Array.from(fileList).slice(0, MAX_FILES);
    setFiles(filesArray);
  };

  const resetFiles = () => {
    setFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedMember) {
      failUpload("请选择上传者");
      return;
    }
    if (files.length === 0) {
      failUpload("请至少选择一张图片");
      return;
    }

    try {
      startUpload();
      
      // 先压缩图片
      setProgress(0);
      const compressedFiles = await compressImages(files, (current, total) => {
        // 压缩进度：0-50%
        setProgress(Math.round((current / total) * 50));
      });
      
      // 然后上传
      const response = await uploadImages(
        selectedMember, 
        compressedFiles, 
        (uploadProgress) => {
          // 上传进度：50-100%
          setProgress(50 + Math.round(uploadProgress / 2));
        }
      );
      
      finishUpload(response);
      resetFiles();
    } catch (err) {
      failUpload(
        err instanceof Error ? err.message : "上传失败，请稍后重试"
      );
    }
  };

  const handleClearAll = async () => {
    const pwd = window.prompt("请输入清空所有记录的密码：");
    if (pwd === null) return;
    if (!pwd) {
      window.alert("密码不能为空。");
      return;
    }
    try {
      await clearAllImages(pwd);
      window.alert("已清空所有记录和图片。");
    } catch (err) {
      window.alert(
        err instanceof Error ? err.message : "清空失败，请检查密码或稍后重试。"
      );
    }
  };

  return (
    <form className="upload-panel" onSubmit={handleSubmit}>
      <div className="field">
        <label htmlFor="uploader">上传人</label>
        <select
          id="uploader"
          value={selectedMember}
          onChange={(event) => setSelectedMember(event.target.value)}
          disabled={isUploading}
        >
          <option value="" disabled>
            请选择
          </option>
          {teamMembers.map((member) => (
            <option key={member} value={member}>
              {member}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor="files">图片（最多 {MAX_FILES} 张）</label>
        <input
          ref={fileInputRef}
          type="file"
          id="files"
          multiple
          accept="image/*"
          disabled={isUploading}
          onChange={handleFileChange}
        />
        {files.length > 0 && (
          <span className="hint">已选择 {files.length} 张</span>
        )}
      </div>

      <div className="actions-row">
        <button
          className={clsx("primary", { loading: isUploading })}
          type="submit"
          disabled={isUploading}
        >
          {isUploading ? "上传中..." : "开始上传"}
        </button>
        <button
          type="button"
          className="chip-button danger-outline"
          onClick={handleClearAll}
        >
          清空所有记录
        </button>
      </div>

      <div className="progress">
        <div className="progress-bar" style={{ width: `${progress}%` }} />
      </div>

      {error && <p className="error">{error}</p>}
    </form>
  );
}

